import { API_BASE_URL } from 'config';
import { authFetch, getAccessToken } from 'api/authFetch';

// Helper function to extract user data from JWT token
const getUserDataFromToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    
    // Extract claims (support both .NET URI claims and standard JWT claims)
    const id =
      payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
      payload.sub ??
      payload.userId ??
      payload.id ??
      payload.nameid ??
      payload.uid ??
      null;

    const name =
      payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ??
      payload.name ??
      payload.unique_name ??
      payload.username ??
      payload.email ??
      null;

    const roleRaw =
      payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? payload.role ?? payload.roles ?? null;
    const role = Array.isArray(roleRaw) ? roleRaw[0] : roleRaw;
    
    return {
      id,
      name,
      role
    };
  } catch (error) {
    console.error('Error extracting user data from token:', error);
    return null;
  }
};

const readErrorDetails = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      const json = await response.json();
      return json?.message || json?.Message || json?.title || (json?.errors ? JSON.stringify(json.errors) : JSON.stringify(json));
    }
    return await response.text();
  } catch {
    return '';
  }
};

// Helper function to extract user ID from JWT token
const getUserIdFromToken = () => {
  const userData = getUserDataFromToken();
  return userData?.id || null;
};

export const getTokenUserData = getUserDataFromToken;

export const userAPI = {
  // Get current logged in user
  getCurrentUser: async () => {
    try {
      const userId = getUserIdFromToken();
      if (!userId) throw new Error('Unable to extract user ID from token');
      
      const response = await authFetch(`${API_BASE_URL}/api/User/${userId}`, { method: 'GET' });
      if (!response.ok) {
        const details = await readErrorDetails(response);
        throw new Error(`Failed to fetch current user (${response.status})${details ? `: ${details}` : ''}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  // Get all users
  getUsers: async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/User`, { method: 'GET' });
      if (!response.ok) {
        const details = await readErrorDetails(response);
        throw new Error(`Failed to fetch users (${response.status})${details ? `: ${details}` : ''}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Login
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/User/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errorMessage || json?.message || 'Login failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  // Register
  register: async (email, password, firstName, lastName, phone = null, role = 'User') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/User/register`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: firstName,
          lastName, 
          email, 
          password, 
          phone,
          role
        })
      });
      if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errorMessage || json?.message || 'Registration failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Error registering:', error);
      throw error;
    }
  },

  // Logout
  logout: async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/User/logout`, { method: 'POST' });
      if (!response.ok) throw new Error('Logout failed');
      return await response.json();
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/User/delete-${userId}`, { method: 'DELETE' });
      if (!response.ok) {
        const details = await readErrorDetails(response);
        throw new Error(`Failed to delete user (${response.status})${details ? `: ${details}` : ''}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }
};
